import { InferenceSession, Tensor } from 'onnxruntime-web';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getModelScale,
  ModelInput,
  getModelFeeds,
  toModelInputPoint,
} from '../../../utils/sam_model';
import { ModelScale } from '../creators/useSAMAnnotationCreator';
import { filterSmallArea, fillSmallHoles } from '../utils';

export interface UseSAMProps {
  imageSize:
    | {
        height: number;
        width: number;
      }
    | null
    | undefined;
  onnxModel: ArrayBuffer | undefined;
  imageEmbedding?: Tensor | undefined;
  highResFeats0?: Tensor | undefined;
  highResFeats1?: Tensor | undefined;
  onError?: (error: unknown) => void;
}

export const useSAM = ({
  imageSize,
  onnxModel,
  imageEmbedding,
  highResFeats0,
  highResFeats1,
  onError,
}: UseSAMProps) => {
  const [modelSession, setModelSession] = useState<InferenceSession | null>(null);

  // The ONNX model expects the input to be rescaled to 1024.
  // The modelScale state variable keeps track of the scale values.
  const [modelScale, setModelScale] = useState<ModelScale | null>(null);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (imageSize?.height !== undefined && imageSize?.width !== undefined) {
      setModelScale(getModelScale(imageSize.height, imageSize.width));
    }
  }, [imageSize?.height, imageSize?.width]);

  // Load SAM model
  useEffect(() => {
    if (onnxModel && !modelSession) {
      import('onnxruntime-web').then(({ InferenceSession }) => {
        InferenceSession.create(onnxModel)
          .then(session => {
            setModelSession(session);
          })
          .catch(error => {
            onErrorRef.current?.(error);
          });
      });
    }
  }, [modelSession, onnxModel]);

  // Run the model on the prompt
  const runModel = useCallback(
    async (prompts: ModelInput[] | null) => {
      if (prompts && modelSession && imageEmbedding && modelScale) {
        try {
          const feeds = await getModelFeeds({
            imageEmbedding,
            highResFeats0,
            highResFeats1,
            prompts: prompts.map(prompt => ({
              ...prompt,
              ...toModelInputPoint(prompt, imageSize),
            })),
            modelScale,
          });

          if (feeds) {
            const results = await modelSession.run(feeds);
            const { masks } = results;
            const [, , maskHeight, maskWidth] = masks.dims;
            const maskSize = maskHeight * maskWidth;
            const maskData = masks.data.slice(0, maskSize);
            const newMasks = new Tensor('float32', maskData, [1, 1, maskHeight, maskWidth]);
            // Apply default post-processing
            const withoutSmallAreas = filterSmallArea(newMasks);
            return fillSmallHoles(withoutSmallAreas);
          }
        } catch (error) {
          onErrorRef.current?.(error);
        }
      }

      return null;
    },
    [modelSession, imageEmbedding, modelScale, highResFeats0, highResFeats1, imageSize],
  );

  return { runModel };
};
